import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { InquiryItem } from "@/types/database";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Send, Printer, Edit } from "lucide-react";
import { usePDF } from "react-to-pdf";

export function InquiryAgentView() {
  const { id } = useParams();

  const { data: inquiry, isLoading } = useQuery<InquiryItem>({
    queryKey: ["inquiry", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("inquiry")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  // Fetch USD rate from settings
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("settings")
        .select("key, value");

      if (error) {
        console.error("Error fetching settings:", error);
        return { usd_rate: "0.000064" }; // Default fallback value
      }

      // Transform array of settings to object
      return data.reduce((acc, curr) => {
        acc[curr.key] = curr.value;
        return acc;
      }, {} as Record<string, string>);
    },
  });

  const { toPDF, targetRef } = usePDF({
    filename: inquiry ? `${inquiry?.name || id}.pdf` : `inquiry.pdf`,
    page: { margin: 20 }
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  if (!inquiry) {
    return <div>Inquiry not found</div>;
  }

  // Format currency
  const formatIDR = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatUSD = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Convert IDR to USD using the rate from settings
  const convertToUSD = (idrValue: number) => {
    const usdRate = parseFloat(settings?.usd_rate || "0.000064");
    return idrValue * usdRate;
  };

  const cleanTitle = (title: string) => {
    if (!title) return title;
    // Remove only transport cost information, keep the area
    return title
      .replace(/ - Transport: (Rp )?[\d.,]+/g, '')  // Format: " - Transport: Rp 135000" or " - Transport: 135000"
      .replace(/ - Transport Cost: [\d.,]+/g, '');   // Format: " - Transport Cost: 135000"
  };

  return (
    <div>
      <div id="inquiry-content" ref={targetRef} className="container mx-auto py-2 space-y-2 max-w-[210mm]">


        <Card className="shadow-sm p-4 bg-primary/5">
          <CardContent className="p-0 flex justify-center">
            <h2 className="text-3xl font-bold text-primary">Voucher Code: {inquiry.voucher_code}</h2>
          </CardContent>
        </Card>

        {/* Guest Information */}
        <Card className="shadow-sm p-1">
          <CardHeader className="p-1">
            <CardTitle className="text-sm font-medium">Guest Information</CardTitle>
          </CardHeader>
          <CardContent className="p-1">
            <Table className="border-collapse border border-gray-800">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Name</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Guest</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Days</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Hotel</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Car Type</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Date</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Flight Information</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Remarks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">{inquiry.name}</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">
                    {inquiry.adult_count} Adult & {inquiry.child_count} Child
                  </TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">{inquiry.nights}</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">{inquiry.hotel_type}</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">{inquiry.car_type || "-"}</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">{format(new Date(inquiry.date), "dd MMM yyyy")}</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800 whitespace-pre-wrap">{inquiry.flight_information || "-"}</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800 whitespace-pre-wrap">{inquiry.remarks || "-"}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Tour Package Information */}
        <Card className="shadow-sm p-1">
          <CardHeader className="p-1">
            <CardTitle className="text-sm font-medium">Tour Package Information</CardTitle>
          </CardHeader>
          <CardContent className="p-1">
            <div className="space-y-2">
              {Object.entries(inquiry.tour_package_json || {}).map(([day, data]) => (
                <div key={day} className="p-2 border border-gray-800 rounded-md">
                  <h3 className="text-xs font-medium mb-2">
                    {(() => {
                      // Get the original title
                      const originalTitle = (data as any).title || (data as any).captured_tours?.[day]?.title;
                      
                      if (originalTitle) {
                        return cleanTitle(originalTitle);
                      }
                      
                      // Fallback if no title exists
                      return `Day ${day.replace('day_', '')} - ${(data as any).tour_package_name || ''}`;
                    })()}
                  </h3>
                  <div className="border rounded-md">
                    <Table className="border-collapse border border-gray-800">
                      <TableHeader>
                        <TableRow className="hover:bg-transparent">
                          <TableHead className="h-4 p-2 text-xs border border-gray-800">Tours</TableHead>
                          <TableHead className="h-4 p-2 text-xs border border-gray-800">Entrance/Adult</TableHead>
                          {inquiry.child_count > 0 && (
                            <TableHead className="h-4 p-2 text-xs border border-gray-800">Entrance/Child</TableHead>
                          )}
                          <TableHead className="h-4 p-2 text-xs border border-gray-800">Parking</TableHead>
                          <TableHead className="h-4 p-2 text-xs border border-gray-800">Transport</TableHead>
                          <TableHead className="h-4 p-2 text-xs border border-gray-800">Meal Cost</TableHead>
                          <TableHead className="h-4 p-2 text-xs border border-gray-800">Other Cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.tours.map((tour, index) => (
                          <TableRow key={index} className="hover:bg-transparent">
                            <TableCell className="h-4 p-2 text-xs border border-gray-800">{tour}</TableCell>
                            <TableCell className="h-4 p-2 text-xs border border-gray-800">{formatIDR(data.entrance_adult[tour] || 0)}</TableCell>
                            {inquiry.child_count > 0 && (
                              <TableCell className="h-4 p-2 text-xs border border-gray-800">{formatIDR(data.entrance_child[tour] || 0)}</TableCell>
                            )}
                            <TableCell className="h-4 p-2 text-xs border border-gray-800">{formatIDR(data.parking[tour] || 0)}</TableCell>
                            <TableCell className="h-4 p-2 text-xs border border-gray-800">
                              {index === 0 ? formatIDR(data.transport_cost) : ''}
                            </TableCell>
                            <TableCell className="h-4 p-2 text-xs border border-gray-800">
                              {index === 0 ? formatIDR(data.meal_cost) : ''}
                            </TableCell>
                            <TableCell className="h-4 p-2 text-xs border border-gray-800">
                              {index === 0 ? formatIDR(data.other_cost) : ''}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {/* Day Notes */}
                  {data.note && (
                    <div className="mt-4">
                      <h4 className="text-xs font-semibold mb-1">Notes:</h4>
                      <p className="text-xs whitespace-pre-line pl-2 border-l-2 border-primary">{String(data.note)}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Hotel Information */}
        <Card className="shadow-sm p-1">
          <CardHeader className="p-1">
            <CardTitle className="text-sm font-medium">Hotel Information</CardTitle>
          </CardHeader>
          <CardContent className="p-1">
            <Table className="border-collapse border border-gray-800">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Hotel Name</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Check-in</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Check-out</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Room Count</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Nights</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Room Rate</TableHead>
                  <TableHead className="h-4 p-2 text-xs border border-gray-800">Total Price</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(inquiry.hotel_json?.hotels || {}).map(([day, hotel]) => (
                  <TableRow key={day} className="hover:bg-transparent">
                    <TableCell className="h-4 p-2 text-xs border border-gray-800">{hotel.hotel_name}</TableCell>
                    <TableCell className="h-4 p-2 text-xs border border-gray-800">{format(new Date(hotel.checkin_date), "dd MMM yyyy")}</TableCell>
                    <TableCell className="h-4 p-2 text-xs border border-gray-800">{format(new Date(hotel.checkout_date), "dd MMM yyyy")}</TableCell>
                    <TableCell className="h-4 p-2 text-xs border border-gray-800">{hotel.room_count}</TableCell>
                    <TableCell className="h-4 p-2 text-xs border border-gray-800">{hotel.nights}</TableCell>
                    <TableCell className="h-4 p-2 text-xs border border-gray-800">{formatIDR((hotel as any).room_rate || 0)}</TableCell>
                    <TableCell className="h-4 p-2 text-xs border border-gray-800">{formatIDR((hotel as any).total_price || 0)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Cost Summary */}
        <Card className="shadow-sm p-1 mt-2">
          <CardHeader className="p-1">
            <CardTitle className="text-sm font-medium">Cost Summary</CardTitle>
          </CardHeader>
          <CardContent className="p-1">
            <Table className="border-collapse border border-gray-800">
              <TableBody>
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">Transport</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800 text-right">{formatIDR(inquiry.total_transport_cost)}</TableCell>
                </TableRow>
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">Parking</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800 text-right">{formatIDR(inquiry.total_parking_cost)}</TableCell>
                </TableRow>
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">Entrance Fee</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800 text-right">{formatIDR(inquiry.total_entrance_fee)}</TableCell>
                </TableRow>
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">Meal</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800 text-right">{formatIDR(inquiry.total_meal_cost)}</TableCell>
                </TableRow>
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">Other</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800 text-right">{formatIDR(inquiry.total_other_cost)}</TableCell>
                </TableRow>
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">Hotel</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800 text-right">{formatIDR(inquiry.total_hotel_price)}</TableCell>
                </TableRow>
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-4 p-2 text-xs font-medium border border-gray-800">Grand Total</TableCell>
                  <TableCell className="h-4 p-2 text-xs font-medium border border-gray-800 text-right">{formatIDR(inquiry.grand_total)}</TableCell>
                </TableRow>
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-4 p-2 text-xs font-medium border border-gray-800 text-primary">Total USD</TableCell>
                  <TableCell className="h-4 p-2 text-xs font-medium border border-gray-800 text-right text-primary">
                    {formatUSD(inquiry.total_usd || 0)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 mt-4 print:hidden sticky bottom-0 w-full bg-white border-t shadow-lg p-4">
        <Button 
          variant="outline" 
          onClick={() => toPDF()}
          className="print:hidden"
        >
          <Download className="w-4 h-4 mr-2" />
          Download PDF
        </Button>
        <Button 
          variant="default" 
          className="print:hidden"
          asChild
        >
          <Link to={`/inquiry/${inquiry.id}/edit`}>
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Link>
        </Button>
      </div>
    </div>
  );
}
